import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Database } from '../../database.types';

type Brand = Database['public']['Tables']['brands']['Row'];

interface BrandsContextType {
    brands: Brand[];
    selectedBrand: Brand | null;
    setSelectedBrand: (brand: Brand | null) => void;
    loading: boolean;
    refreshBrands: () => Promise<void>;
}

const BrandsContext = createContext<BrandsContextType>({
    brands: [],
    selectedBrand: null,
    setSelectedBrand: () => { },
    loading: true,
    refreshBrands: async () => { }
});

export function BrandsProvider({ children }: { children: React.ReactNode }) {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [brands, setBrands] = useState<Brand[]>([]);
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
    const [loading, setLoading] = useState(true);

    // Helper function to get brandId from URL
    const getBrandIdFromUrl = useCallback(() => {
        const brandId = searchParams.get('brandId');
        console.log('[DEBUG] BrandsProvider - getBrandIdFromUrl:', {
            brandId,
            searchParams: Array.from(searchParams.entries()),
            location: location.pathname + location.search
        });
        return brandId;
    }, [searchParams, location]);

    const loadBrands = useCallback(async () => {
        try {
            setLoading(true);
            // console.log('[DEBUG] BrandsProvider - loadBrands started');
            
            // Get current user first
            const { data: { user } } = await supabase.auth.getUser();
            // console.log('[DEBUG] BrandsProvider - user loaded:', { userId: user?.id });
            if (!user) {
                console.log('[DEBUG] BrandsProvider - no user, setting empty brands');
                setBrands([]);
                setLoading(false);
                return;
            }

            // Fetch brands the user has access to through their account
            const { data: accountBrands, error: accountError } = await supabase
                .from('brands')
                .select('*')
                .neq('status', 'DELETED')
                .eq('status', 'ACTIVE')  // Only show ACTIVE brands
                .order('brand_name');

            if (accountError) throw accountError;

            // Fetch brands the user has direct access to
            const { data: directAccessBrands, error: directError } = await supabase
                .from('brand_access')
                .select(`
                    access_level,
                    expires_at,
                    brand:brands!inner(
                        id,
                        brand_name,
                        account_id,
                        status
                    )
                `)
                .eq('user_id', user.id)
                .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString());

            if (directError) throw directError;

            // Combine both sets of brands
            const brandMap = new Map();

            // Add account brands (already filtered for ACTIVE status)
            accountBrands?.forEach(brand => {
                brandMap.set(brand.id, brand);
            });

            // Add or update with direct access brands (filter for ACTIVE status)
            directAccessBrands?.forEach(access => {
                const brand = access.brand;
                // Only include brands that are ACTIVE
                if (brand.status === 'ACTIVE' && !brandMap.has(brand.id)) {
                    // This is a brand from another account
                    brandMap.set(brand.id, brand);
                }
            });

            const brandsData = Array.from(brandMap.values());

            const validBrands = brandsData.filter(brand =>
                brand.brand_name && brand.brand_name.trim() !== '' && brand.status === 'ACTIVE'
            ).sort((a, b) => a.brand_name.localeCompare(b.brand_name));

            // console.log('[DEBUG] BrandsProvider - valid brands loaded:', { 
            //     count: validBrands.length, 
            //     brandIds: validBrands.map(b => ({ id: b.id, name: b.brand_name }))
            // });

            setBrands(validBrands);

            // Priority order for brand selection:
            // 1. brandId from URL query parameter (highest priority)
            // 2. storedBrandId from localStorage 
            // 3. first available brand (fallback)
            
            const urlBrandId = getBrandIdFromUrl();
            const storedBrandId = localStorage.getItem('selectedBrandId');
            
            console.log('[DEBUG] BrandsProvider - brand selection sources:', {
                urlBrandId,
                storedBrandId,
                availableBrands: validBrands.length
            });

            let brandToSelect: Brand | null = null;
            let selectionSource = '';

            // First priority: URL brandId parameter
            if (urlBrandId && validBrands.length > 0) {
                const urlBrand = validBrands.find(b => b.id === urlBrandId);
                if (urlBrand) {
                    brandToSelect = urlBrand;
                    selectionSource = 'URL parameter';
                    console.log('[DEBUG] BrandsProvider - found brand from URL:', { 
                        brandId: urlBrand.id, 
                        brandName: urlBrand.brand_name 
                    });
                } else {
                    console.log('[DEBUG] BrandsProvider - brandId from URL not found in available brands:', {
                        requestedId: urlBrandId,
                        availableIds: validBrands.map(b => b.id)
                    });
                }
            }

            // Second priority: stored brand from localStorage
            if (!brandToSelect && storedBrandId && validBrands.length > 0) {
                const storedBrand = validBrands.find(b => b.id === storedBrandId);
                if (storedBrand) {
                    brandToSelect = storedBrand;
                    selectionSource = 'localStorage';
                    console.log('[DEBUG] BrandsProvider - found brand from localStorage:', { 
                        brandId: storedBrand.id, 
                        brandName: storedBrand.brand_name 
                    });
                }
            }

            // Third priority: first available brand
            if (!brandToSelect && validBrands.length > 0) {
                brandToSelect = validBrands[0];
                selectionSource = 'first available';
                console.log('[DEBUG] BrandsProvider - selecting first available brand:', { 
                    brandId: brandToSelect?.id, 
                    brandName: brandToSelect?.brand_name 
                });
            }

            // Set the selected brand and update localStorage
            if (brandToSelect) {
                console.log('[DEBUG] BrandsProvider - setting selected brand:', {
                    source: selectionSource,
                    brandId: brandToSelect?.id,
                    brandName: brandToSelect?.brand_name
                });
                setSelectedBrand(brandToSelect);
                localStorage.setItem('selectedBrandId', brandToSelect.id);
            } else {
                console.log('[DEBUG] BrandsProvider - no brand selected, no brands available');
                setSelectedBrand(null);
                localStorage.removeItem('selectedBrandId');
            }
        } catch (error) {
            console.error('Error loading brands:', error);
        } finally {
            setLoading(false);
        }
    }, [getBrandIdFromUrl]); // Include getBrandIdFromUrl to watch for URL changes

    useEffect(() => {
        loadBrands();
    }, [loadBrands]);

    // Separate effect to handle URL parameter changes when brands are already loaded
    useEffect(() => {
        if (brands.length > 0 && !loading) {
            const urlBrandId = getBrandIdFromUrl();
            console.log('[DEBUG] BrandsProvider - URL change effect triggered:', {
                urlBrandId,
                currentSelectedBrand: selectedBrand?.id,
                brandsLoaded: brands.length
            });

            if (urlBrandId) {
                const urlBrand = brands.find(b => b.id === urlBrandId);
                if (urlBrand && urlBrand.id !== selectedBrand?.id) {
                    console.log('[DEBUG] BrandsProvider - switching to URL brand:', {
                        from: selectedBrand?.id,
                        to: urlBrand.id,
                        brandName: urlBrand.brand_name
                    });
                    setSelectedBrand(urlBrand);
                    localStorage.setItem('selectedBrandId', urlBrand.id);
                } else if (!urlBrand) {
                    console.log('[DEBUG] BrandsProvider - URL brandId not found in loaded brands:', {
                        requestedId: urlBrandId,
                        availableIds: brands.map(b => b.id)
                    });
                }
            }
        }
    }, [getBrandIdFromUrl, brands, loading, selectedBrand]);

    const handleSetSelectedBrand = (brand: Brand | null) => {
        console.log('[DEBUG] BrandsProvider - handleSetSelectedBrand called:', {
            previousBrand: selectedBrand?.id,
            newBrand: brand?.id,
            brandName: brand?.brand_name
        });
        setSelectedBrand(brand);
        if (brand) {
            localStorage.setItem('selectedBrandId', brand.id);
        } else {
            localStorage.removeItem('selectedBrandId');
        }
    };

    const refreshBrands = useCallback(async () => {
        await loadBrands();
    }, [loadBrands]);

    return (
        <BrandsContext.Provider value={{ brands, selectedBrand, setSelectedBrand: handleSetSelectedBrand, loading, refreshBrands }}>
            {children}
        </BrandsContext.Provider>
    );
}

export function useBrands() {
    const context = useContext(BrandsContext);
    if (!context) {
        throw new Error('useBrands must be used within a BrandsProvider');
    }
    return context;
}
